/**
 * Media time as a first-class domain concept.
 *
 * Positions and durations are integer ticks. One tick is a "flick"
 * (1/705,600,000 s), which divides evenly into every common film, video and
 * audio rate, including the NTSC 1000/1001 family. Frame rates are rationals.
 * We never accumulate floating-point seconds; conversions to seconds exist only
 * at the edges (browser <video>.currentTime, ffmpeg arguments).
 */

export const TICKS_PER_SECOND = 705_600_000;

export interface Rational {
  num: number;
  den: number;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

export function rational(num: number, den = 1): Rational {
  if (!Number.isInteger(num) || !Number.isInteger(den) || den === 0) {
    throw new RangeError(`Invalid rational ${num}/${den}`);
  }
  const sign = den < 0 ? -1 : 1;
  const g = gcd(num, den);
  return { num: (sign * num) / g, den: (sign * den) / g };
}

/** Parse ffprobe-style rationals: "30000/1001", "25/1", "25", "0/0". */
export function parseRational(value: string | undefined | null): Rational | null {
  if (!value) return null;
  const [n, d = '1'] = value.split('/');
  const num = Number(n);
  const den = Number(d);
  if (!Number.isFinite(num) || !Number.isFinite(den) || num <= 0 || den <= 0) return null;
  if (Number.isInteger(num) && Number.isInteger(den)) return rational(num, den);
  // Decimal rates such as "29.97" – snap to a known broadcast rate when close.
  return snapFrameRate(num / den);
}

const KNOWN_RATES: Rational[] = [
  { num: 24000, den: 1001 }, { num: 24, den: 1 }, { num: 25, den: 1 },
  { num: 30000, den: 1001 }, { num: 30, den: 1 }, { num: 48, den: 1 },
  { num: 50, den: 1 }, { num: 60000, den: 1001 }, { num: 60, den: 1 },
];

export function snapFrameRate(fps: number): Rational {
  for (const r of KNOWN_RATES) {
    if (Math.abs(r.num / r.den - fps) < 0.002) return r;
  }
  return rational(Math.round(fps * 1000), 1000);
}

export function rationalToNumber(r: Rational): number {
  return r.num / r.den;
}

export function formatRational(r: Rational): string {
  return r.den === 1 ? `${r.num}` : `${r.num}/${r.den}`;
}

/** Human label: 29.97, 25, 23.976 */
export function frameRateLabel(r: Rational): string {
  const v = r.num / r.den;
  return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, '');
}

/** Ticks per frame, exact when the rate divides TICKS_PER_SECOND. */
export function ticksPerFrame(rate: Rational): number {
  return (TICKS_PER_SECOND * rate.den) / rate.num;
}

export function framesToTicks(frames: number, rate: Rational): number {
  return Math.round((frames * TICKS_PER_SECOND * rate.den) / rate.num);
}

/** Frame index containing the tick position (floor). */
export function ticksToFrames(ticks: number, rate: Rational): number {
  return Math.floor((ticks * rate.num) / (TICKS_PER_SECOND * rate.den) + 1e-9);
}

export function secondsToTicks(seconds: number): number {
  return Math.round(seconds * TICKS_PER_SECOND);
}

export function ticksToSeconds(ticks: number): number {
  return ticks / TICKS_PER_SECOND;
}

export function isDropFrameRate(rate: Rational): boolean {
  return rate.den === 1001 && (rate.num === 30000 || rate.num === 60000);
}

export interface Timecode {
  hours: number;
  minutes: number;
  seconds: number;
  frames: number;
  dropFrame: boolean;
}

/** Nominal integer frames per second used for timecode counting (e.g. 30 for 29.97). */
export function timecodeBase(rate: Rational): number {
  return Math.round(rate.num / rate.den);
}

/** SMPTE timecode for a frame count, using drop-frame counting for 29.97/59.94. */
export function framesToTimecode(totalFrames: number, rate: Rational, dropFrame = isDropFrameRate(rate)): Timecode {
  const base = timecodeBase(rate);
  let frames = Math.max(0, Math.floor(totalFrames));
  if (dropFrame) {
    const dropPerMinute = base === 60 ? 4 : 2;
    const framesPer10Min = base * 600 - dropPerMinute * 9;
    const framesPerMin = base * 60 - dropPerMinute;
    const tens = Math.floor(frames / framesPer10Min);
    const rem = frames % framesPer10Min;
    const extra = rem > dropPerMinute ? dropPerMinute * Math.floor((rem - dropPerMinute) / framesPerMin) : 0;
    frames += dropPerMinute * 9 * tens + extra;
  }
  const ff = frames % base;
  const totalSeconds = Math.floor(frames / base);
  return {
    hours: Math.floor(totalSeconds / 3600) % 24,
    minutes: Math.floor(totalSeconds / 60) % 60,
    seconds: totalSeconds % 60,
    frames: ff,
    dropFrame,
  };
}

export function formatTimecode(tc: Timecode): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(tc.hours)}:${p(tc.minutes)}:${p(tc.seconds)}${tc.dropFrame ? ';' : ':'}${p(tc.frames)}`;
}

/** Parse "HH:MM:SS:FF" or "HH:MM:SS;FF" into a frame count at the given rate. */
export function parseTimecode(value: string, rate: Rational): number | null {
  const m = /^(\d{1,2}):(\d{2}):(\d{2})([:;.])(\d{2,3})$/.exec(value.trim());
  if (!m) return null;
  const [, h, mi, s, sep, f] = m;
  const base = timecodeBase(rate);
  const hours = Number(h), minutes = Number(mi), seconds = Number(s), frames = Number(f);
  if (minutes > 59 || seconds > 59 || frames >= base) return null;
  const drop = sep === ';';
  let total = ((hours * 60 + minutes) * 60 + seconds) * base + frames;
  if (drop && isDropFrameRate(rate)) {
    const dropPerMinute = base === 60 ? 4 : 2;
    const totalMinutes = hours * 60 + minutes;
    total -= dropPerMinute * (totalMinutes - Math.floor(totalMinutes / 10));
  }
  return total;
}

/**
 * 1.9.1: timecode frames in 24 hours at a rate: base × 86,400, less the dropped numbers with drop-frame counting
 * (a timecode label wraps to 00:00:00:00 there).
 */
export function timecodeDayFrames(rate: Rational, dropFrame = isDropFrameRate(rate)): number {
  const base = timecodeBase(rate);
  if (!dropFrame) return base * 86_400;
  const dropPerMinute = base === 60 ? 4 : 2;
  return base * 86_400 - dropPerMinute * (1440 - 144);
}

/**
 * 1.9.1, past midnight: a timecode's frames counted on from a start timecode. A recording from 23:30 to 00:40 shows
 * 00:10 for a moment after midnight; read back, a timecode earlier than the start is on the next day, so this adds a
 * day to it. Labels still wrap at 24 hours (framesToTimecode).
 */
export function framesAfterStart(frames: number, startFrames: number, rate: Rational, dropFrame = isDropFrameRate(rate), spanFrames?: number | null): number {
  if (frames >= startFrames) return frames;
  const next = frames + timecodeDayFrames(rate, dropFrame);
  // With the media's length: only when the next day still falls within it (else a timecode before the start stays so).
  return spanFrames != null && spanFrames > 0 && next - startFrames > spanFrames ? frames : next;
}

/**
 * parseTimecode, read as at or after `startFrames` (framesAfterStart); null when it is not a timecode. With
 * `spanFrames` (the media's length in frames), a timecode before the start moves to the next day only when it then
 * falls within the media.
 */
export function parseTimecodeAfter(value: string, rate: Rational, startFrames: number, spanFrames?: number | null): number | null {
  const f = parseTimecode(value, rate);
  return f === null ? null : framesAfterStart(f, startFrames, rate, /;/.test(value) && isDropFrameRate(rate), spanFrames);
}

/**
 * A source timecode (programme marks, Log's In and Out) as ticks of media time from a start timecode, never before 0,
 * past midnight as parseTimecodeAfter with the media's length; null when it is not a timecode.
 */
export function sourceTimecodeTicks(value: string, rate: Rational, startFrames: number, durationTicks?: number | null): number | null {
  const f = parseTimecodeAfter(value, rate, startFrames, durationTicks ? ticksToFrames(durationTicks, rate) + 1 : null);
  return f === null ? null : framesToTicks(Math.max(0, f - startFrames), rate);
}

/** The same for tick positions counted from midnight (NLE check-in, multicam): `dayTicks` is 24 hours of them. */
export function ticksAfterStart(ticks: number, startTicks: number, dayTicks = 86_400 * TICKS_PER_SECOND): number {
  return ticks < startTicks ? ticks + dayTicks : ticks;
}

/**
 * 1.9.1, time-of-day timecode: a clock time as a start timecode at `rate`, snapped to the nearest frame. Drop-frame rates (29.97, 59.94) count frames from real seconds and write `;`, so the label stays on
 * the clock; other rates count base frames a second (23.976 shows the clock at the start). `secondsOfDay` is the
 * local time as seconds since midnight (date-schedule.ts wallClockTimecode reads it from an instant and a zone).
 */
export function timeOfDayTimecode(secondsOfDay: number, rate: Rational): string {
  const drop = isDropFrameRate(rate);
  const perSecond = drop ? rate.num / rate.den : timecodeBase(rate);
  const day = timecodeDayFrames(rate, drop);
  const frames = ((Math.round(secondsOfDay * perSecond) % day) + day) % day;
  return formatTimecode(framesToTimecode(frames, rate, drop));
}

/** Timecode string for a tick offset from a source start timecode. */
export function ticksToTimecodeString(ticks: number, rate: Rational, startFrames = 0): string {
  return formatTimecode(framesToTimecode(startFrames + ticksToFrames(ticks, rate), rate));
}

/** Compact duration label: 1:02:03 or 2:03 */
export function formatDuration(ticks: number): string {
  const total = Math.floor(ticks / TICKS_PER_SECOND);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}
